import { useState, useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { staffService, StaffAvailableSlot, CalendarBlock } from '@/services/api/staff.service';
import { staffAvailabilityService, WeekScheduleDay, AvailabilityOverride, type GroupClassCancelScope } from '@/services/api/availability.service';
import {
  CreateAvailabilityWizard,
  type RecurringData,
  type DateRangeData,
  type OneTimeData,
} from '@/components/availability/CreateAvailabilityWizard';
import { EditAvailabilityDialog, type UpdateFutureData, type CreateMultiDayData, type UpdateGroupData } from '@/components/availability/EditAvailabilityDialog';
import { EditOverrideDialog, type UpdateOverrideData } from '@/components/availability/EditOverrideDialog';
import { EditGroupClassSlotDialog } from '@/components/availability/EditGroupClassSlotDialog';
import { waitlistService } from '@/services/api/waitlist.service';
import type { StaffGroupClassSlotDetail } from '@/services/api/availability.service';
import type { ProviderAvailability } from '@/services/api/availability.service';
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { FloatingActionButton } from '@/components/ui/floating-action-button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { format, startOfWeek, addDays, addWeeks, subWeeks, startOfMonth, endOfMonth, addMonths, subMonths, startOfDay, endOfDay, eachDayOfInterval, isSameMonth, parseISO } from 'date-fns';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { ChevronLeft, ChevronRight, LayoutGrid, CalendarDays, Clock, User, Package, Ban, Check, Pencil, Trash2, Plus, X, CheckCircle, UserCheck, Lock, Layers, AlertCircle, Sunrise, Sun, Moon, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { useResponsiveBreakpoints } from '@/hooks/useMediaQuery';
import { StaffMobileCalendar } from '@/components/schedule/StaffMobileCalendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSetPageHeader } from '@/components/chrome/PageHeader';
import { findOverlappingSlots } from '@/components/schedule/ScheduleGrid';
import { Calendar } from '@/components/calendar/Calendar';
import type {
  CalendarEvent,
  CalendarLayer,
  CalendarLayerInterval,
} from '@/lib/calendar/types';
import {
  slotsToEvents,
  groupClassIntervals,
  availabilityIntervals,
  overrideIntervals,
  calendarBlockIntervals,
} from '@/lib/calendar/slots';
import { bookingsToEvents } from '@/lib/calendar/bookings';

type ViewMode = 'day' | 'week' | 'month' | 'list';


export default function StaffSchedulePage() {
  const { t } = useTranslation(['staff', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const settingsHref = user?.role === 'admin'
    ? '/admin/settings#booking'
    : '/staff/settings#booking-settings';
  const { isMobile, isTablet, isDesktop } = useResponsiveBreakpoints();
  const [viewMode, setViewMode] = useState<ViewMode>(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'day' : 'week'));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSlotForAction, setSelectedSlotForAction] = useState<StaffAvailableSlot | null>(null);
  // Density classes — locked to "comfortable". The compact/spacious
  // branches were never wired to a setter; collapse to the active map.
  const densityClasses = {
    container: 'space-y-2',
    slot: 'p-3',
    time: 'text-sm',
    service: 'text-sm',
    badge: 'text-xs px-2 py-1',
    icon: 'h-4 w-4',
    button: 'h-8 w-8',
  };

  // Calculate date ranges based on view mode
  const getDateRange = () => {
    const locale = getDateLocale();
    switch (viewMode) {
      case 'day':
        return {
          start: startOfDay(currentDate),
          end: endOfDay(currentDate),
          display: format(currentDate, getDateFormats().full, { locale }),
        };
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 6);
        return {
          start: weekStart,
          end: weekEnd,
          display: `${format(weekStart, getDateFormats().dayMonth, { locale })} - ${format(weekEnd, getDateFormats().medium, { locale })}`,
        };
      case 'month':
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        return {
          start: monthStart,
          end: monthEnd,
          display: format(currentDate, getDateFormats().monthYear, { locale }),
        };
      case 'list':
        const listStart = startOfDay(new Date());
        const listEnd = addDays(listStart, 30);
        return {
          start: listStart,
          end: listEnd,
          display: t('schedule.viewModes.upcoming'),
        };
    }
  };

  const dateRange = getDateRange();
  const startDate = format(dateRange.start, 'yyyy-MM-dd');
  const endDate = format(dateRange.end, 'yyyy-MM-dd');

  const { data: slotsResponse, isLoading } = useQuery({
    queryKey: ['staff-schedule', viewMode, startDate, endDate],
    queryFn: () => staffService.getAvailableSlots({ start_date: startDate, end_date: endDate }),
  });

  const slots = slotsResponse?.data || [];

  // Fetch bookings for the date range (for availability-based bookings that don't have slots)
  const { data: bookingsResponse } = useQuery({
    queryKey: ['staff-schedule-bookings', viewMode, startDate, endDate],
    queryFn: () => staffService.getBookings({ start_date: startDate, end_date: endDate }),
  });

  // Group-class participants are surfaced via the violet group-class slot
  // block (N/M counter, click → participants tab). Drop them from the
  // schedule's per-booking timeline so they don't double-render.
  const bookings = (bookingsResponse?.data || []).filter(
    (b) => (b.service?.max_capacity ?? 1) <= 1
  );

  // Calculate all week start dates for the current view (month view needs all weeks)
  const weekStartDates = useMemo(() => {
    if (viewMode !== 'month') return [startDate];
    const dates: string[] = [];
    let current = startOfWeek(dateRange.start, { weekStartsOn: 1 });
    const end = dateRange.end;
    while (current <= end) {
      dates.push(format(current, 'yyyy-MM-dd'));
      current = addDays(current, 7);
    }
    return dates;
  }, [viewMode, startDate, dateRange.start, dateRange.end]);

  // Fetch availability for displaying green blocks (fetches all weeks in month view)
  const weekScheduleQueries = useQueries({
    queries: weekStartDates.map((weekStart) => ({
      queryKey: ['staff-availability-week', weekStart],
      queryFn: () => staffAvailabilityService.getWeekSchedule(weekStart),
    })),
  });

  // Derive weekSchedule from first query for backward compatibility
  const weekSchedule = weekScheduleQueries[0]?.data || [];

  // Build date-to-availability map from all fetched weeks
  const availabilityByDate = useMemo(() => {
    const map = new Map<string, ProviderAvailability[]>();
    weekScheduleQueries.forEach((query, queryIndex) => {
      if (!query.data) return;
      const weekStart = startOfWeek(new Date(weekStartDates[queryIndex] + 'T12:00:00'), { weekStartsOn: 1 });
      query.data.forEach((day: WeekScheduleDay) => {
        const date = addDays(weekStart, (day.day_of_week + 6) % 7);
        const dateStr = format(date, 'yyyy-MM-dd');
        if (!map.has(dateStr)) {
          map.set(dateStr, day.time_ranges);
        }
      });
    });
    return map;
  }, [weekScheduleQueries.map(q => q.data), weekStartDates]);

  // Fetch availability overrides (one-time availability, day off, modified hours)
  const { data: availabilityOverrides = [] } = useQuery({
    queryKey: ['staff-availability-overrides', startDate, endDate],
    queryFn: () => staffAvailabilityService.getOverrides({ start_date: startDate, end_date: endDate }),
  });

  // Fetch calendar blocked slots (Google Calendar busy times)
  const { data: calendarBlocks = [] } = useQuery({
    queryKey: ['staff-calendar-blocks', startDate, endDate],
    queryFn: () => staffService.getCalendarBlocks(startDate, endDate),
  });

  // Calculate stats for dashboard
  const stats = {
    available: slots.filter(s => s.available && !s.booked).length,
    booked: slots.filter(s => s.booked).length,
    blocked: slots.filter(s => !s.available && !s.booked).length,
    pending: slots.filter(s => s.booked && s.booking?.status === 'pending').length,
  };

  // Find slots that need attention (pending or have overlaps)
  const pendingSlots = slots.filter(s => s.booked && s.booking?.status === 'pending');

  // Group slots by start time to find overlapping groups
  const getOverlappingGroups = () => {
    const groups: { startTime: string; slots: StaffAvailableSlot[] }[] = [];
    const processed = new Set<number>();

    slots.forEach(slot => {
      if (processed.has(slot.id)) return;
      const overlaps = findOverlappingSlots(slot, slots);
      if (overlaps.length > 0) {
        const groupSlots = [slot, ...overlaps];
        groupSlots.forEach(s => processed.add(s.id));
        groups.push({
          startTime: slot.start_time || '',
          slots: groupSlots,
        });
      }
    });

    return groups;
  };
  const overlappingGroups = getOverlappingGroups();

  // Calculate days to display based on view
  const getDaysToDisplay = () => {
    switch (viewMode) {
      case 'day':
        return [currentDate];
      case 'week':
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
      case 'month':
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
        const calendarEnd = addDays(startOfWeek(monthEnd, { weekStartsOn: 1 }), 6);
        return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
      case 'list':
        return []; // List view doesn't need days array - slots are rendered chronologically
    }
  };

  const daysToDisplay = getDaysToDisplay();

  const blockMutation = useMutation({
    mutationFn: (id: number) => staffService.blockSlot(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['staff-schedule'] });
      toast({
        title: t('common:toasts.success'),
        description: t('schedule.actions.blockSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: error.response?.data?.message || t('schedule.actions.blockError'),
        variant: 'destructive',
      });
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (id: number) => staffService.unblockSlot(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['staff-schedule'] });
      toast({
        title: t('common:toasts.success'),
        description: t('schedule.actions.unblockSuccess'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: error.response?.data?.message || t('schedule.actions.unblockError'),
        variant: 'destructive',
      });
    },
  });

  // Delete slot mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => staffService.deleteSlot(id),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['staff-schedule'] });
      toast({
        title: t('common:toasts.success'),
        description: t('common:toasts.slotDeletedSuccess'),
      });
      setDeleteConfirmSlot(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: error.response?.data?.error || t('common:toasts.failedToDeleteSlot'),
        variant: 'destructive',
      });
    },
  });

  // Helper to translate backend error messages to localized versions
  const translateBackendError = (errorMsg: string): string => {
    // Check for "Overlaps with existing availability for {day}" pattern
    const overlapMatch = errorMsg.match(/Overlaps with existing availability for (\w+)/i);
    if (overlapMatch) {
      const dayName = overlapMatch[1];
      return t('availability.errors.overlapsWithExisting', { day: dayName });
    }

    // Map known backend error messages to translation keys
    const errorMappings: Record<string, string> = {
      'must be after start time': 'availability.errors.endTimeAfterStart',
      'End time must be after start time': 'availability.errors.endTimeAfterStart',
      'is required for monthly recurrence': 'availability.errors.monthlyWeekRequired',
      'Monthly week is required for monthly recurrence': 'availability.errors.monthlyWeekRequired',
      'Monthly day is required for monthly recurrence': 'availability.errors.monthlyDayRequired',
      'is required when ending on a specific date': 'availability.errors.endDateRequired',
      'End date is required when ending on a specific date': 'availability.errors.endDateRequired',
      'is required when ending after a number of occurrences': 'availability.errors.occurrencesRequired',
      'Occurrences is required when ending after a number of occurrences': 'availability.errors.occurrencesRequired',
      'already has an active override for this provider': 'availability.errors.dateAlreadyHasOverride',
      'Date already has an active override for this provider': 'availability.errors.dateAlreadyHasOverride',
      'End date must be after start date': 'availability.errors.endDateAfterStart',
    };

    // Check for exact match
    for (const [pattern, translationKey] of Object.entries(errorMappings)) {
      if (errorMsg.toLowerCase().includes(pattern.toLowerCase())) {
        return t(translationKey);
      }
    }

    // Return original message if no translation found
    return errorMsg;
  };

  // Helper to extract detailed error messages from backend response
  const extractErrorMessage = (error: any, fallback: string): string => {
    const errorData = error.response?.data;
    if (!errorData) return fallback;

    // Backend wraps data in render_json_response, so check both levels
    const data = errorData.data || errorData;

    // Check for validation_errors (slot conflicts) - can be at root or in data
    const validationErrors = data.validation_errors || errorData.validation_errors;
    if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
      // Rails validation errors have 'base' array with error messages
      const firstError = validationErrors[0];
      if (firstError.base && Array.isArray(firstError.base) && firstError.base.length > 0) {
        // Extract just the error message from base
        const errorMsg = translateBackendError(firstError.base[0]);
        if (validationErrors.length > 1) {
          return `${errorMsg} (+${validationErrors.length - 1} more slots with same issue)`;
        }
        return errorMsg;
      }
      // Fallback to other formats
      const conflicts = validationErrors.slice(0, 3);
      let message = conflicts.map((e: any) => {
        const rawMsg = e.error || e.message || (e.base ? e.base[0] : JSON.stringify(e));
        return translateBackendError(rawMsg);
      }).join('; ');
      if (validationErrors.length > 3) {
        message += ` (+${validationErrors.length - 3} more)`;
      }
      return message;
    }

    // Check for errors array (detailed error messages)
    const errors = data.errors || errorData.errors;
    if (errors && Array.isArray(errors) && errors.length > 0) {
      return errors.map((e: string) => translateBackendError(e)).join('; ');
    }

    // Check for main error message
    if (errorData.error) return translateBackendError(errorData.error);
    if (data.error) return translateBackendError(data.error);

    // Check for message field
    if (data.message) return translateBackendError(data.message);
    if (errorData.message) return translateBackendError(errorData.message);

    return fallback;
  };

  // Availability mutations
  const createMultiDayAvailabilityMutation = useMutation({
    mutationFn: staffAvailabilityService.createMultiDayAvailability,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      // Navigate to effective_from date if it's in the future
      if (data.length > 0) {
        const firstRecord = data[0];
        if (firstRecord.effective_from) {
          const effectiveDate = new Date(firstRecord.effective_from);
          if (effectiveDate > new Date()) {
            setCurrentDate(effectiveDate);
          }
        }
      }
      toast({ title: t('availability.toasts.createdRecords', { count: data.length }) });
      setShowAvailabilityDialog(false);
      setConflictDialogData(null);
    },
    onError: (error: any, variables) => {
      const errorData = error.response?.data;
      if (errorData?.conflict && errorData?.conflicts) {
        setSkipWizardReset(true);
        setShowAvailabilityDialog(false);
        setConflictDialogData({
          conflicts: errorData.conflicts,
          pendingRequest: variables as RecurringData,
        });
        return;
      }
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToCreate')),
        variant: 'destructive'
      });
    }
  });

  const createDateRangeAvailabilityMutation = useMutation({
    mutationFn: staffAvailabilityService.createDateRangeAvailability,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({ title: t('availability.toasts.createdDateRange', { count: data.length }) });
      setShowAvailabilityDialog(false);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToCreateDateRange')),
        variant: 'destructive'
      });
    }
  });

  const updateFutureAvailabilityMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFutureData }) =>
      staffAvailabilityService.updateFutureAvailability(id, {
        day_of_week: data.day_of_week,
        start_time: data.start_time,
        end_time: data.end_time,
        recurrence_type: data.recurrence_type,
        recurrence_interval: data.recurrence_interval,
        monthly_week: data.monthly_week,
        monthly_day: data.monthly_day,
        end_type: data.end_type,
        end_date: data.end_date,
        occurrences: data.occurrences,
        service_ids: data.service_ids,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      toast({ title: t('availability.toasts.updatedFuture') });
      setShowEditAvailabilityDialog(false);
      setSelectedAvailability(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToUpdate')),
        variant: 'destructive'
      });
    }
  });

  const deleteAvailabilityMutation = useMutation({
    mutationFn: (id: number) => staffAvailabilityService.deleteAvailability(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      toast({ title: t('availability.toasts.deleted') });
      setShowEditAvailabilityDialog(false);
      setSelectedAvailability(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToDelete')),
        variant: 'destructive'
      });
    }
  });

  const deleteAvailabilityGroupMutation = useMutation({
    mutationFn: (groupId: string) => staffAvailabilityService.deleteGroup(groupId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      toast({ title: t('availability.toasts.deletedRecords', { count: result.count }) });
      setShowEditAvailabilityDialog(false);
      setSelectedAvailability(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToDeleteGroup')),
        variant: 'destructive'
      });
    }
  });

  const updateAvailabilityGroupMutation = useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: Parameters<typeof staffAvailabilityService.updateGroup>[1] }) =>
      staffAvailabilityService.updateGroup(groupId, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      toast({ title: t('availability.toasts.updatedRecords', { count: result.count }) });
      setShowEditAvailabilityDialog(false);
      setSelectedAvailability(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToUpdateGroup')),
        variant: 'destructive'
      });
    }
  });

  const markUnavailableMutation = useMutation({
    mutationFn: ({ date, recurrenceGroupId, startTime, endTime }: { date: string; recurrenceGroupId?: string; startTime?: string; endTime?: string }) => staffAvailabilityService.markDateUnavailable(date, undefined, recurrenceGroupId, startTime, endTime),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({ title: t('availability.toasts.markedUnavailable') });
      setShowEditAvailabilityDialog(false);
      setSelectedAvailability(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToDelete')),
        variant: 'destructive'
      });
    }
  });

  // Override mutations
  const updateOverrideMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Parameters<typeof staffAvailabilityService.updateOverride>[1] }) =>
      staffAvailabilityService.updateOverride(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({ title: t('availability.toasts.updated') });
      setShowEditOverrideDialog(false);
      setSelectedOverride(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToUpdate')),
        variant: 'destructive'
      });
    }
  });

  const deleteOverrideMutation = useMutation({
    mutationFn: (id: number) => staffAvailabilityService.deleteOverride(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({ title: t('availability.toasts.deleted') });
      setShowEditOverrideDialog(false);
      setSelectedOverride(null);
    },
    onError: (error: any) => {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToDelete')),
        variant: 'destructive'
      });
    }
  });

  // Handlers for availability creation
  const handleCreateRecurringAvailability = async (data: RecurringData) => {
    await createMultiDayAvailabilityMutation.mutateAsync(data);
  };

  const handleCreateDateRangeAvailability = async (data: DateRangeData) => {
    await createDateRangeAvailabilityMutation.mutateAsync(data);
  };

  const handleCreateOneTimeAvailability = async (data: OneTimeData) => {
    try {
      await staffAvailabilityService.createOverride({
        date: data.date,
        override_type: 'extra',
        start_time: data.start_time,
        end_time: data.end_time,
        reason: data.reason,
        service_ids: data.service_ids,
      });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({ title: t('availability.toasts.oneTimeCreated') });
      setShowAvailabilityDialog(false);
    } catch (error: any) {
      const errorData = error.response?.data;
      if (errorData?.conflict && errorData?.conflicts) {
        setSkipWizardReset(true);
        setShowAvailabilityDialog(false);
        setOneTimeConflictData({
          conflicts: errorData.conflicts,
          pendingRequest: {
            date: data.date,
            start_time: data.start_time,
            end_time: data.end_time,
            reason: data.reason,
            service_ids: data.service_ids,
          },
        });
        return;
      }
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToCreate')),
        variant: 'destructive',
      });
    }
  };

  // Group-class handlers — these dispatch to the new staff endpoints. The
  // server materialises AvailableSlot rows per occurrence, so the schedule
  // shows empty 0/N classes immediately on success.
  const handleCreateGroupClassRecurring = async (data: import('@/components/availability/CreateAvailabilityWizard').GroupRecurringData) => {
    try {
      const svc = services.find((s) => s.id === data.service_id);
      const result = await staffAvailabilityService.createGroupClassRecurringAvailability({
        service_id: data.service_id,
        days_of_week: data.days_of_week,
        start_time: data.start_time,
        recurrence_type: data.recurrence_type,
        recurrence_interval: data.recurrence_interval,
        monthly_week: data.monthly_week,
        monthly_day: data.monthly_day,
        end_type: data.end_type,
        end_date: data.end_date,
        occurrences: data.occurrences,
        effective_from: data.effective_from,
      });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({
        title: t('common:toasts.createSuccess'),
        description: t(
          'availability.groupClass.toasts.created',
          'Created {{count}} {{service}} classes ({{capacity}} spots each)',
          {
            count: result.slots_created,
            service: svc?.name ?? 'group',
            capacity: svc?.max_capacity ?? 0,
          }
        ),
      });
      setShowAvailabilityDialog(false);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToCreate')),
        variant: 'destructive',
      });
    }
  };

  const handleCreateGroupClassOneTime = async (data: import('@/components/availability/CreateAvailabilityWizard').GroupOneTimeData) => {
    try {
      const svc = services.find((s) => s.id === data.service_id);
      await staffAvailabilityService.createGroupClassOneTimeAvailability({
        service_id: data.service_id,
        date: data.date,
        start_time: data.start_time,
      });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({
        title: t('common:toasts.createSuccess'),
        description: t(
          'availability.groupClass.toasts.createdOne',
          'Created 1 {{service}} class ({{capacity}} spots)',
          {
            service: svc?.name ?? 'group',
            capacity: svc?.max_capacity ?? 0,
          }
        ),
      });
      setShowAvailabilityDialog(false);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: extractErrorMessage(error, t('availability.errors.failedToCreate')),
        variant: 'destructive',
      });
    }
  };

  // Handlers for availability edit/delete
  const handleAvailabilityClick = (availability: ProviderAvailability, date?: string) => {
    setSelectedAvailability(availability);
    setSelectedAvailabilityDate(date || null);
    setShowEditAvailabilityDialog(true);
  };

  const handleOverrideClick = (override: AvailabilityOverride) => {
    setSelectedOverride(override);
    setShowEditOverrideDialog(true);
  };

  const handleEditGroupClass = async (slot: StaffAvailableSlot) => {
    // Cancelled slots open a "Remove from schedule" confirmation instead of
    // the edit dialog (the slot is no longer editable).
    if (slot.cancelled) {
      setRemoveCancelledSlotId(slot.id);
      return;
    }
    setEditGroupClassSlotId(slot.id);
    setEditGroupClassDetail(null);
    setLoadingGroupClassDetail(true);
    try {
      const detail = await staffAvailabilityService.getGroupClassSlot(slot.id);
      setEditGroupClassDetail(detail);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error?.response?.data?.error || t('availability.errors.failedToLoad', 'Failed to load class'),
        variant: 'destructive',
      });
      setEditGroupClassSlotId(null);
    } finally {
      setLoadingGroupClassDetail(false);
    }
  };

  const [removeCancelledSlotId, setRemoveCancelledSlotId] = useState<number | null>(null);
  const [removingCancelledSlot, setRemovingCancelledSlot] = useState(false);

  const handleConfirmRemoveCancelled = async () => {
    if (!removeCancelledSlotId) return;
    setRemovingCancelledSlot(true);
    try {
      await staffAvailabilityService.removeGroupClassSlot(removeCancelledSlotId);
      queryClient.invalidateQueries({ queryKey: ['staff-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      toast({
        title: t('staff:availability.groupClass.toasts.removed', 'Class removed from schedule.'),
      });
      setRemoveCancelledSlotId(null);
    } catch (error: any) {
      toast({
        title: t('common:toasts.error'),
        description: error?.response?.data?.error
          || t('common:errors.deleteFailed', { resource: 'class' }),
        variant: 'destructive',
      });
    } finally {
      setRemovingCancelledSlot(false);
    }
  };

  const closeGroupClassDialog = () => {
    setEditGroupClassSlotId(null);
    setEditGroupClassDetail(null);
  };

  const handleSaveGroupClassSlot = async (params: { date: string; start_time: string }) => {
    if (!editGroupClassSlotId) return;
    setSavingGroupClassSlot(true);
    try {
      await staffAvailabilityService.updateGroupClassSlot(
        editGroupClassSlotId,
        params,
      );
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({
        title: t('availability.groupClass.toasts.updated', 'Class updated.'),
      });
      closeGroupClassDialog();
    } catch (error: any) {
      const message = error?.response?.data?.errors?.[0]
        || error?.response?.data?.error
        || t('availability.errors.failedToUpdate', 'Failed to update class');
      toast({
        title: t('common:toasts.error'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSavingGroupClassSlot(false);
    }
  };

  const handleCancelGroupClassSlot = async (scope: GroupClassCancelScope) => {
    if (!editGroupClassSlotId) return;
    setCancellingGroupClassSlot(true);
    try {
      const result = await staffAvailabilityService.cancelGroupClassSlot(editGroupClassSlotId, scope);
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      queryClient.invalidateQueries({ queryKey: ['staff-availability-overrides'] });
      toast({
        title: result.cancelled_count > 1
          ? t('availability.groupClass.toasts.cancelledMany', '{{count}} classes cancelled.', {
              count: result.cancelled_count,
            })
          : t('availability.groupClass.toasts.cancelled', 'Class cancelled.'),
      });
      closeGroupClassDialog();
    } catch (error: any) {
      const message = error?.response?.data?.errors?.[0]
        || error?.response?.data?.error
        || t('availability.errors.failedToDelete', 'Failed to cancel class');
      toast({
        title: t('common:toasts.error'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setCancellingGroupClassSlot(false);
    }
  };

  const handleUpdateOverride = async (id: number, data: UpdateOverrideData) => {
    await updateOverrideMutation.mutateAsync({ id, data });
  };

  const handleDeleteOverride = async (id: number) => {
    await deleteOverrideMutation.mutateAsync(id);
  };

  const handleUpdateFutureAvailability = async (id: number, data: UpdateFutureData) => {
    await updateFutureAvailabilityMutation.mutateAsync({ id, data });
  };

  // Handler for creating additional days from the edit dialog
  const handleCreateMultiDayFromEdit = async (data: CreateMultiDayData) => {
    await staffAvailabilityService.createMultiDayAvailability(data);
    queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
  };

  const handleDeleteAvailability = async (id: number) => {
    await deleteAvailabilityMutation.mutateAsync(id);
  };

  const handleDeleteAvailabilityGroup = async (groupId: string, fromDate?: string) => {
    if (fromDate) {
      await staffAvailabilityService.endGroup(groupId, fromDate);
      queryClient.invalidateQueries({ queryKey: ['staff-availability-week'] });
      setShowEditAvailabilityDialog(false);
    } else {
      await deleteAvailabilityGroupMutation.mutateAsync(groupId);
    }
  };

  const handleUpdateAvailabilityGroup = async (groupId: string, data: Parameters<typeof staffAvailabilityService.updateGroup>[1]) => {
    await updateAvailabilityGroupMutation.mutateAsync({ groupId, data });
  };

  // Dialog state
  const [showAvailabilityDialog, setShowAvailabilityDialog] = useState(false);
  const [wizardPrefillDateTime, setWizardPrefillDateTime] = useState<{ date: Date; startTime: string } | null>(null);
  const [availabilityDialogInitialStep, setAvailabilityDialogInitialStep] = useState<0 | 1 | 2 | 3 | 4 | 5 | undefined>(undefined);
  const [skipWizardReset, setSkipWizardReset] = useState(false);
  const [conflictDialogData, setConflictDialogData] = useState<{
    conflicts: Array<{
      day_of_week: number;
      day_name: string;
      conflicting: Array<{ id: number; start_time: string; end_time: string; recurrence_summary: string; services?: Array<{ id: number; name: string }> }>;
    }>;
    pendingRequest: RecurringData;
  } | null>(null);
  const [oneTimeConflictData, setOneTimeConflictData] = useState<{
    conflicts: Array<{ start_time: string; end_time: string; recurrence_summary: string }>;
    pendingRequest: { date: string; start_time: string; end_time: string; reason?: string; service_ids?: number[] };
  } | null>(null);
  const [showEditAvailabilityDialog, setShowEditAvailabilityDialog] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState<ProviderAvailability | null>(null);
  const [selectedAvailabilityDate, setSelectedAvailabilityDate] = useState<string | null>(null);
  const [showEditOverrideDialog, setShowEditOverrideDialog] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState<AvailabilityOverride | null>(null);
  const [deleteConfirmSlot, setDeleteConfirmSlot] = useState<number | null>(null);
  // Group-class slot edit dialog. We hold both the slot id (so the dialog
  // can stay open while we fetch the detail with participants) and the
  // detail itself once loaded. Cancellation is server-enforced — refuses
  // when there are active bookings.
  const [editGroupClassSlotId, setEditGroupClassSlotId] = useState<number | null>(null);
  const [editGroupClassDetail, setEditGroupClassDetail] = useState<StaffGroupClassSlotDetail | null>(null);
  const [loadingGroupClassDetail, setLoadingGroupClassDetail] = useState(false);
  const [savingGroupClassSlot, setSavingGroupClassSlot] = useState(false);
  const [cancellingGroupClassSlot, setCancellingGroupClassSlot] = useState(false);

  const waitlistEnabledForOpenSlot = editGroupClassDetail?.waitlist_enabled === true;
  const { data: waitlistEntries = [], isLoading: loadingWaitlist } = useQuery({
    queryKey: ['staff-waitlist-entries', editGroupClassSlotId],
    queryFn: () => waitlistService.adminListBySlot(editGroupClassSlotId!),
    enabled: editGroupClassSlotId !== null && waitlistEnabledForOpenSlot,
  });

  const handleRemoveWaitlistEntry = async (entryId: number) => {
    try {
      await waitlistService.adminRemove(entryId);
      queryClient.invalidateQueries({ queryKey: ['staff-waitlist-entries', editGroupClassSlotId] });
      toast({
        title: t('common:success'),
        description: t('waitlist:admin.removeSuccess', 'Waitlist entry removed.'),
      });
    } catch (err: any) {
      toast({
        title: t('common:errors.generic'),
        description: err?.message || t('common:errors.generic'),
        variant: 'destructive',
      });
    }
  };

  // Fetch services assigned to the current provider
  const { data: services = [] } = useQuery({
    queryKey: ['staff-my-services'],
    queryFn: () => staffAvailabilityService.getMyServices(),
  });

  const handleDeleteSlot = (slotId: number, booked: boolean) => {
    if (booked) {
      toast({ title: t('common:toasts.error'), description: t('schedule.actions.cannotDeleteBooked'), variant: 'destructive' });
      return;
    }
    setDeleteConfirmSlot(slotId);
  };

  const handlePrevious = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, -1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
    }
  };

  const handleNext = () => {
    switch (viewMode) {
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Handle clicking a day in month view - navigate to that day's view
  const handleDayClick = (date: Date) => {
    setCurrentDate(date);
    setViewMode('day');
  };

  // Handler for mobile slot edit action - opens action dialog
  const handleMobileEditSlot = (slot: StaffAvailableSlot) => {
    setSelectedSlotForAction(slot);
  };

  // ── Unified Calendar wiring (day/week views) ─────────────────────────
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const fallback = t('schedule.available', 'Available');
    // Slot-backed bookings render via slotsToEvents (the slot tile shows the
    // customer name). Drop them from the bookings stream so the same booking
    // doesn't render twice on the calendar.
    const slotBookingIds = new Set<number>(
      slots.map((s) => s.booking?.id).filter((id): id is number => typeof id === 'number'),
    );
    const directBookings = (bookings as any[]).filter((b) => !slotBookingIds.has(b.id));
    return [
      ...slotsToEvents(slots, fallback),
      ...bookingsToEvents(directBookings),
    ];
  }, [slots, bookings, t]);

  const calendarLayers: CalendarLayer[] = useMemo(() => {
    const overrides = overrideIntervals(availabilityOverrides);
    return [
      {
        id: 'availability',
        variant: 'available',
        intervals: availabilityIntervals(availabilityByDate),
        onIntervalClick: (i: CalendarLayerInterval) => {
          const d = i.data as { kind: string; dateStr: string; range: ProviderAvailability } | undefined;
          if (d) handleAvailabilityClick(d.range, d.dateStr);
        },
      },
      {
        id: 'override-available',
        variant: 'override-available',
        intervals: overrides.available,
        onIntervalClick: (i) => {
          const d = i.data as { override: AvailabilityOverride } | undefined;
          if (d) handleOverrideClick(d.override);
        },
      },
      {
        id: 'override-blocked',
        variant: 'override-blocked',
        intervals: overrides.blocked,
        onIntervalClick: (i) => {
          const d = i.data as { override: AvailabilityOverride } | undefined;
          if (d) handleOverrideClick(d.override);
        },
      },
      {
        id: 'group-class',
        variant: 'group-class',
        intervals: groupClassIntervals(slots),
        onIntervalClick: (i) => {
          const d = i.data as { slot: StaffAvailableSlot } | undefined;
          if (d) handleEditGroupClass(d.slot);
        },
      },
      {
        id: 'calendar-busy',
        variant: 'busy',
        intervals: calendarBlockIntervals(calendarBlocks, t('schedule.calendarBlock.busyShort')),
      },
    ];
  }, [availabilityByDate, availabilityOverrides, slots, calendarBlocks]);

  const handleCalendarEventClick = (e: CalendarEvent) => {
    const m = e.meta as { kind?: string; slot?: StaffAvailableSlot; booking?: any } | undefined;
    if (!m) return;
    if (m.kind === 'slot' && m.slot) {
      if (m.slot.booked && m.slot.booking?.public_id) {
        setLocation(`/staff/bookings/${m.slot.booking.public_id}`);
        return;
      }
      setSelectedSlotForAction(m.slot);
      return;
    }
    if (m.kind === 'booking' && m.booking) {
      const id = m.booking.publicId ?? m.booking.public_id ?? m.booking.id;
      setLocation(`/staff/bookings/${id}`);
    }
  };

  const handleCalendarSlotClick = (s: { start: Date; columnId: string }) => {
    // A month cell is a whole day, not a time slot — drill into it instead of
    // opening the availability dialog at midnight.
    if (viewMode === 'month') {
      setCurrentDate(s.start);
      setViewMode('day');
      return;
    }
    setWizardPrefillDateTime({ date: s.start, startTime: format(s.start, 'HH:mm') });
    setShowAvailabilityDialog(true);
  };

  useSetPageHeader(
    {
      title: t('schedule.title'),
      sub: t('schedule.subtitle'),
      actions: isDesktop ? (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="md"
            onClick={() => (window.location.href = settingsHref)}
            title={t('common:nav.settings')}
            iconLeft={<Settings className="h-4 w-4" />}
          >
            {t('common:nav.settings')}
          </Button>
          <Button size="md" onClick={() => setShowAvailabilityDialog(true)} iconLeft={<Plus className="h-4 w-4" />}>
            {t('schedule.addAvailability')}
          </Button>
        </div>
      ) : undefined,
    },
    [t, isDesktop, settingsHref],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Mobile/Tablet Calendar View — header + tabs in single card, internal scroll */}
      {!isDesktop && (
        <div
          className={cn(
            'bg-white rounded-lg border border-line overflow-hidden flex flex-col',
            // Day/week scroll inside a viewport-sized card; month grows to its
            // full grid so the page scrolls instead of nesting two scrollers.
            viewMode === 'month' ? 'h-auto' : 'h-[calc(100dvh-7rem)]',
          )}
        >
          <div className="border-b border-line p-3 shrink-0">
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
              <TabsList className="w-full">
                <TabsTrigger value="day" className="flex-1">{t('schedule.viewModes.day')}</TabsTrigger>
                <TabsTrigger value="week" className="flex-1">{t('schedule.viewModes.week')}</TabsTrigger>
                <TabsTrigger value="month" className="flex-1">{t('schedule.viewModes.month')}</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <StaffMobileCalendar
            viewMode={viewMode}
            currentDate={currentDate}
            slots={slots}
            bookings={bookings}
            weekSchedule={weekSchedule}
            availabilityOverrides={availabilityOverrides}
            calendarBlocks={calendarBlocks}
            onCreateSlot={() => setShowAvailabilityDialog(true)}
            onSlotClick={handleMobileEditSlot}
            onBookingClick={(b) => setLocation(`/staff/bookings/${b.id}`)}
            onAvailabilityClick={handleAvailabilityClick}
            onOverrideClick={handleOverrideClick}
            onEditGroupClass={handleEditGroupClass}
            onDateChange={setCurrentDate}
            onViewModeChange={setViewMode}
          />
        </div>
      )}

      {/* Desktop Calendar View */}
      {isDesktop && (
      <div className="bg-white rounded-lg border border-line overflow-hidden">
        <Calendar
          view={viewMode === 'list' ? 'day' : viewMode}
          date={currentDate}
          onDateChange={setCurrentDate}
          events={calendarEvents}
          layers={calendarLayers}
          loading={isLoading}
          hideViewTabs
          toolbar={
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
              <TabsList>
                <TabsTrigger value="day">{t('schedule.viewModes.day')}</TabsTrigger>
                <TabsTrigger value="week">{t('schedule.viewModes.week')}</TabsTrigger>
                <TabsTrigger value="month">{t('schedule.viewModes.month')}</TabsTrigger>
              </TabsList>
            </Tabs>
          }
          onEventClick={handleCalendarEventClick}
          onSlotClick={handleCalendarSlotClick}
        />
      </div>
      )}


      {/* Legend - Hidden on mobile */}
      {isDesktop && (
      <div className="bg-surface rounded-lg border border-line p-4">
        <h3 className="text-mono-eyebrow uppercase text-soft mb-3">{t('schedule.legend.title')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-emerald-50 border border-emerald-200 rounded-md flex-shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink">{t('schedule.legend.available.title')}</p>
              <p className="text-[11px] text-soft">{t('schedule.legend.available.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-blue-50 border border-brand-500 rounded-md flex-shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink">{t('schedule.legend.booked.title')}</p>
              <p className="text-[11px] text-soft">{t('schedule.legend.booked.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-amber-50 border border-amber-200 rounded-md flex-shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink">{t('schedule.legend.pending.title')}</p>
              <p className="text-[11px] text-soft">{t('schedule.legend.pending.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-red-50 border border-red-200 rounded-md flex-shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink">{t('schedule.legend.blocked.title')}</p>
              <p className="text-[11px] text-soft">{t('schedule.legend.blocked.description')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 bg-surface-2 border border-line rounded-md flex-shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-ink">{t('schedule.legend.calendarBusy.title')}</p>
              <p className="text-[11px] text-soft">{t('schedule.legend.calendarBusy.description')}</p>
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-[11px] text-amber-800">
            <strong>Note:</strong> {t('schedule.legend.note')}
          </p>
        </div>
      </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmSlot !== null} onOpenChange={() => setDeleteConfirmSlot(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('schedule.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('schedule.deleteDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmSlot(null)}>
              {t('schedule.deleteDialog.buttons.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteConfirmSlot && deleteMutation.mutate(deleteConfirmSlot)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('schedule.deleteDialog.buttons.deleting') : t('schedule.deleteDialog.buttons.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Slot Action Dialog */}
      <Dialog open={selectedSlotForAction !== null} onOpenChange={() => setSelectedSlotForAction(null)}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{t('schedule.slotActions.title')}</DialogTitle>
            <DialogDescription>
              {selectedSlotForAction && (
                <div className="mt-2 space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-soft" />
                    <span>
                      {selectedSlotForAction.start_time && format(parseISO(selectedSlotForAction.start_time), 'HH:mm')} - {selectedSlotForAction.end_time && format(parseISO(selectedSlotForAction.end_time), 'HH:mm')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-soft" />
                    <span>{selectedSlotForAction.service?.name || t('schedule.unknownService')}</span>
                  </div>
                  <Badge
                    tone={
                      selectedSlotForAction.booked
                        ? (selectedSlotForAction.booking?.status === 'pending' ? 'warn' : 'info')
                        : selectedSlotForAction.available ? 'success' : 'danger'
                    }
                    className="mt-2"
                  >
                    {selectedSlotForAction.booked
                      ? (selectedSlotForAction.booking?.status === 'pending' ? t('bookings:status.pending') : t('schedule.status.booked'))
                      : selectedSlotForAction.available ? t('schedule.status.available') : t('schedule.status.blocked')}
                  </Badge>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-4">
            {selectedSlotForAction && !selectedSlotForAction.booked && (
              <>
                {selectedSlotForAction.available ? (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-amber-600 hover:bg-amber-50"
                    onClick={() => {
                      blockMutation.mutate(selectedSlotForAction.id);
                      setSelectedSlotForAction(null);
                    }}
                    disabled={blockMutation.isPending}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    {t('schedule.actions.blockSlot')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="w-full justify-start text-emerald-600 hover:bg-emerald-50"
                    onClick={() => {
                      unblockMutation.mutate(selectedSlotForAction.id);
                      setSelectedSlotForAction(null);
                    }}
                    disabled={unblockMutation.isPending}
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {t('schedule.actions.unblockSlot')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start text-red-600 hover:bg-red-50"
                  onClick={() => {
                    setDeleteConfirmSlot(selectedSlotForAction.id);
                    setSelectedSlotForAction(null);
                  }}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('schedule.actions.deleteSlot')}
                </Button>
              </>
            )}
            {selectedSlotForAction?.booked && selectedSlotForAction?.booking?.id && (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setLocation(`/staff/bookings/${selectedSlotForAction.booking?.public_id}`);
                  setSelectedSlotForAction(null);
                }}
              >
                <User className="h-4 w-4 mr-2" />
                {t('schedule.actions.viewBooking')}
              </Button>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setSelectedSlotForAction(null)} className="w-full">
              {t('schedule.actions.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Availability - Wizard for both mobile and desktop */}
      <CreateAvailabilityWizard
        open={showAvailabilityDialog}
        onOpenChange={(open) => { setShowAvailabilityDialog(open); if (!open) { setAvailabilityDialogInitialStep(undefined); setSkipWizardReset(false); setWizardPrefillDateTime(null); } }}
        initialStep={availabilityDialogInitialStep}
        skipResetOnClose={skipWizardReset}
        initialDateTime={wizardPrefillDateTime}
        onCreateRecurring={handleCreateRecurringAvailability}
        onCreateDateRange={handleCreateDateRangeAvailability}
        onCreateOneTime={handleCreateOneTimeAvailability}
        onCreateGroupRecurring={handleCreateGroupClassRecurring}
        onCreateGroupOneTime={handleCreateGroupClassOneTime}
        services={services.map(s => ({
          id: s.id,
          name: s.name,
          active: s.active,
          max_capacity: s.max_capacity ?? undefined,
          duration_minutes: s.duration_minutes,
        }))}
      />

      {/* Edit Availability Dialog */}
      <EditAvailabilityDialog
        open={showEditAvailabilityDialog}
        onOpenChange={setShowEditAvailabilityDialog}
        availability={selectedAvailability}
        allAvailabilities={weekSchedule.flatMap((day: WeekScheduleDay) => day.time_ranges || [])}
        services={services
          .filter(s => selectedAvailability?.kind === 'group_class'
            ? (s.max_capacity ?? 1) > 1
            : (s.max_capacity ?? 1) === 1)
          .map(s => ({ id: s.id, name: s.name, active: s.active }))}
        selectedDate={selectedAvailabilityDate || undefined}
        onUpdateFuture={handleUpdateFutureAvailability}
        onCreateMultiDay={handleCreateMultiDayFromEdit}
        onUpdateGroup={handleUpdateAvailabilityGroup}
        onDelete={handleDeleteAvailability}
        onDeleteGroup={handleDeleteAvailabilityGroup}
        onDeleteSingleDay={async (date, recurrenceGroupId, startTime, endTime) => { await markUnavailableMutation.mutateAsync({ date, recurrenceGroupId, startTime, endTime }); }}
      />

      {/* Edit Override Dialog (One-time availability) */}
      <EditOverrideDialog
        open={showEditOverrideDialog}
        onOpenChange={setShowEditOverrideDialog}
        override={selectedOverride}
        services={services
          .filter(s => (s.max_capacity ?? 1) === 1)
          .map(s => ({ id: s.id, name: s.name, active: s.active }))}
        onUpdate={handleUpdateOverride}
        onDelete={handleDeleteOverride}
      />

      {/* Group-class slot edit dialog (Details + Participants tabs) */}
      <EditGroupClassSlotDialog
        open={editGroupClassSlotId !== null}
        onOpenChange={(open) => { if (!open) closeGroupClassDialog(); }}
        detail={editGroupClassDetail}
        isLoading={loadingGroupClassDetail}
        onSave={handleSaveGroupClassSlot}
        onCancelClass={handleCancelGroupClassSlot}
        isSaving={savingGroupClassSlot}
        isCancelling={cancellingGroupClassSlot}
        onViewParticipant={(publicId) => setLocation(`/staff/bookings/${publicId}`)}
        onOpenFullPage={(slotId) => setLocation(`/staff/group-classes/${slotId}`)}
        waitlistEntries={waitlistEntries}
        isLoadingWaitlist={loadingWaitlist}
        onRemoveWaitlistEntry={handleRemoveWaitlistEntry}
      />

      {/* Cancelled-class remove confirmation */}
      <AlertDialog
        open={removeCancelledSlotId !== null}
        onOpenChange={(open) => { if (!open && !removingCancelledSlot) setRemoveCancelledSlotId(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('staff:availability.groupClass.removeConfirm.title', 'This class is cancelled')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'staff:availability.groupClass.removeConfirm.description',
                'Removing it deletes the slot from your schedule. Participants were already notified by email.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removingCancelledSlot}>
              {t('staff:availability.groupClass.removeConfirm.keep', 'Keep on schedule')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 text-white hover:bg-rose-700"
              disabled={removingCancelledSlot}
              onClick={(e) => { e.preventDefault(); handleConfirmRemoveCancelled(); }}
            >
              {t('staff:availability.groupClass.removeConfirm.confirm', 'Remove from schedule')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Availability Conflict Dialog */}
      <Dialog open={!!conflictDialogData} onOpenChange={(open) => { if (!open) setConflictDialogData(null); }}>
        <DialogContent className="sm:max-w-[540px] max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('availability.conflicts.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 py-1">
            {/* Current (existing) availability */}
            {conflictDialogData?.conflicts.map((conflict) => (
              <div key={conflict.day_of_week} className="space-y-2">
                {conflict.conflicting.map((item) => (
                  <div key={item.id} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1">{t('availability.conflicts.currentLabel')}</p>
                    <p className="text-sm font-medium text-amber-900">
                      {item.services?.map(s => s.name).join(', ') || '—'}{' · '}
                      {item.recurrence_summary ? `${item.recurrence_summary} ${item.start_time} – ${item.end_time}` : `${conflict.day_name} ${item.start_time} – ${item.end_time}`}
                    </p>
                  </div>
                ))}
              </div>
            ))}

            {/* New availability being created */}
            {conflictDialogData && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-1">{t('availability.conflicts.newLabel')}</p>
                <p className="text-sm font-medium text-blue-900">
                  {conflictDialogData.pendingRequest.service_ids?.length
                    ? services.filter(s => conflictDialogData.pendingRequest.service_ids?.includes(s.id)).map(s => s.name).join(', ')
                    : '—'}{' · '}
                  {conflictDialogData.conflicts.map(c => c.day_name).join(', ')}{' · '}
                  {conflictDialogData.pendingRequest.start_time} – {conflictDialogData.pendingRequest.end_time}
                </p>
              </div>
            )}

            {/* Explanation */}
            <p className="text-sm text-soft">
              {t('availability.conflicts.explanation')}
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setConflictDialogData(null);
                setAvailabilityDialogInitialStep(4);
                setShowAvailabilityDialog(true);
              }}
              className="flex-1"
            >
              {t('availability.conflicts.editAction')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setConflictDialogData(null)}
              className="flex-1"
            >
              {t('availability.conflicts.skipAction')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (conflictDialogData) {
                  createMultiDayAvailabilityMutation.mutate({
                    ...conflictDialogData.pendingRequest,
                    force_override: true,
                  });
                }
              }}
              disabled={createMultiDayAvailabilityMutation.isPending}
              className="flex-1"
            >
              {t('availability.conflicts.overrideAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time Availability Conflict Dialog */}
      <Dialog open={!!oneTimeConflictData} onOpenChange={(open) => { if (!open) setOneTimeConflictData(null); }}>
        <DialogContent className="sm:max-w-[540px] max-h-[90dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              {t('availability.conflicts.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1 py-1">
            {/* Current (existing) recurring availabilities */}
            {oneTimeConflictData?.conflicts.map((item, index) => (
              <div key={index} className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-600 mb-1">{t('availability.conflicts.currentLabel')}</p>
                <p className="text-sm font-medium text-amber-900">
                  {item.recurrence_summary ? `${item.recurrence_summary} ${item.start_time} – ${item.end_time}` : `${item.start_time} – ${item.end_time}`}
                </p>
              </div>
            ))}

            {/* New one-time availability being created */}
            {oneTimeConflictData && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-1">{t('availability.conflicts.newLabel')}</p>
                <p className="text-sm font-medium text-blue-900">
                  {oneTimeConflictData.pendingRequest.service_ids?.length
                    ? services.filter(s => oneTimeConflictData.pendingRequest.service_ids?.includes(s.id)).map(s => s.name).join(', ')
                    : '—'}{' · '}
                  {oneTimeConflictData.pendingRequest.date}{' · '}
                  {oneTimeConflictData.pendingRequest.start_time} – {oneTimeConflictData.pendingRequest.end_time}
                </p>
              </div>
            )}

            <p className="text-sm text-soft">
              {t('availability.conflicts.explanation')}
            </p>
          </div>

          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOneTimeConflictData(null);
                setAvailabilityDialogInitialStep(3);
                setShowAvailabilityDialog(true);
              }}
              className="flex-1"
            >
              {t('availability.conflicts.editAction')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setOneTimeConflictData(null)}
              className="flex-1"
            >
              {t('availability.conflicts.skipAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FloatingActionButton
        icon={<Plus className="w-5 h-5" />}
        label={t('schedule.addAvailability')}
        onClick={() => setShowAvailabilityDialog(true)}
      />
    </div>
  );
}
